if (!customElements.get('cart-quick-view')) {
  class CartQuickView extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeyup = this.onKeyup.bind(this);
      this.onItemAdded = this.onItemAdded.bind(this);
      this.onVariantResolved = this.onVariantResolved.bind(this);
    }

    connectedCallback() {
      this.productEl = this.querySelector('.cart-quick-view__product');
      this.contentEl = this.querySelector('.cart-quick-view__content');
      this.addEventListener('click', this.onClick);
      document.addEventListener('keyup', this.onKeyup);
      document.addEventListener('cart:item-added', this.onItemAdded);
      document.addEventListener('bundle:variant-resolved', this.onVariantResolved);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('keyup', this.onKeyup);
      document.removeEventListener('cart:item-added', this.onItemAdded);
      document.removeEventListener('bundle:variant-resolved', this.onVariantResolved);
    }

    onVariantResolved(event) {
      if (!this.classList.contains('is-open')) return;
      this.classList.toggle('is-resolved', !!(event.detail && event.detail.resolved));
    }

    onClick(event) {
      if (event.target.closest('.cart-quick-view__close') || event.target.closest('.cart-quick-view__back')) {
        this.close();
        return;
      }
      if (event.target === this) this.close();
    }

    onKeyup(event) {
      if (event.code === 'Escape' && this.classList.contains('is-open')) {
        event.stopPropagation();
        this.close();
      }
    }

    onItemAdded() {
      if (this.classList.contains('is-open')) this.close();
    }

    async open(productUrl, card) {
      const token = (this.openToken = (this.openToken || 0) + 1);
      this.openerEl = document.activeElement;
      this.fillProductHeader(card);
      this.classList.remove('is-resolved');
      this.classList.add('is-open', 'is-loading');
      this.setAttribute('data-picker-scope-active', '');
      document.body.classList.add('quick-view-open');
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.dispatchEvent(new Event('transitionend'));
      const closeButton = this.querySelector('.cart-quick-view__close');
      if (typeof trapFocus === 'function' && closeButton) trapFocus(this, closeButton);
      try {
        const responseText = await this.fetchProduct(productUrl);
        if (token !== this.openToken || !this.classList.contains('is-open')) return;
        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        await this.adoptStylesheets(doc);
        if (token !== this.openToken || !this.classList.contains('is-open')) return;
        const section = doc.querySelector('section[id^="MainProduct-"]');
        if (!section) throw new Error('MainProduct section not found at ' + productUrl);
        section.querySelectorAll('.product__media-wrapper').forEach((el) => el.remove());
        this.namespaceSection(section);
        const wrapper = document.createElement('div');
        wrapper.className = 'cart-quick-view__pdp';
        wrapper.setAttribute('data-picker-inline', '');
        wrapper.appendChild(section);
        this.contentEl.querySelectorAll('.cart-quick-view__pdp').forEach((el) => el.remove());
        this.contentEl.appendChild(wrapper);
        if (typeof trapFocus === 'function' && closeButton) trapFocus(this, closeButton);
        this.openMobileSheet(wrapper);
      } catch (error) {
        console.error('cart-quick-view: failed to load product', error);
        if (token === this.openToken) {
          this.close();
          if (productUrl) window.location.href = productUrl;
        }
      } finally {
        if (token === this.openToken) this.classList.remove('is-loading');
      }
    }

    close() {
      this.classList.remove('is-open');
      this.removeAttribute('data-picker-scope-active');
      document.body.classList.remove('quick-view-open');
      if (this.sheetObserver) {
        this.sheetObserver.disconnect();
        this.sheetObserver = null;
      }
      this.querySelector('picker-mobile-sheet[open]')?.removeAttribute('open');
      document.querySelectorAll('.variant-options-drawer.is-active').forEach((el) => el.classList.remove('is-active'));
      document.querySelector('.drawer-overlay.is-active')?.classList.remove('is-active');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openerEl);
      const drawer = document.querySelector('cart-drawer.active');
      if (drawer && typeof trapFocus === 'function') {
        const drawerInner = drawer.querySelector('.drawer__inner');
        if (drawerInner) trapFocus(document.getElementById('CartDrawer') || drawer, drawerInner);
      }
      setTimeout(() => {
        if (!this.classList.contains('is-open')) {
          this.contentEl.querySelectorAll('.cart-quick-view__pdp').forEach((el) => el.remove());
          this.productEl.innerHTML = '';
        }
      }, 350);
    }

    fillProductHeader(card) {
      this.productEl.innerHTML = '';
      if (!card) return;
      const media = card.querySelector('.complete-setup__media');
      const name = card.querySelector('.complete-setup__name');
      const rating = card.querySelector('.complete-setup__rating');
      const prices = card.querySelector('.complete-setup__prices');
      if (media) this.productEl.appendChild(media.cloneNode(true));
      const info = document.createElement('div');
      info.className = 'cart-quick-view__product-info';
      [name, rating, prices].forEach((el) => {
        if (el) info.appendChild(el.cloneNode(true));
      });
      this.productEl.appendChild(info);
    }

    fetchProduct(url) {
      if (!this.productCache) this.productCache = new Map();
      if (!this.productCache.has(url)) {
        const request = fetch(url)
          .then((response) => response.text())
          .catch((error) => {
            this.productCache.delete(url);
            throw error;
          });
        this.productCache.set(url, request);
      }
      return this.productCache.get(url);
    }

    prefetch(url) {
      if (!url) return;
      this.fetchProduct(url)
        .then((responseText) => this.adoptStylesheets(new DOMParser().parseFromString(responseText, 'text/html')))
        .catch(() => {});
    }

    openMobileSheet(wrapper) {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      const sheet = wrapper.querySelector('picker-mobile-sheet');
      if (!sheet) return;
      if (this.sheetObserver) this.sheetObserver.disconnect();
      this.sheetObserver = new MutationObserver(() => {
        if (!sheet.hasAttribute('open') && this.classList.contains('is-open') && !this.classList.contains('is-resolved')) {
          this.close();
        }
      });
      customElements.whenDefined('picker-mobile-sheet').then(() => {
        requestAnimationFrame(() => {
          if (!this.classList.contains('is-open') || typeof sheet.open !== 'function') return;
          sheet.open();
          const sheetPanel = sheet.querySelector('.pdp-sheet__panel');
          if (sheetPanel && !sheetPanel.querySelector('.cart-quick-view__sheet-product')) {
            const header = this.productEl.cloneNode(true);
            header.classList.add('cart-quick-view__sheet-product');
            sheetPanel.prepend(header);
          }
          this.sheetObserver.observe(sheet, { attributes: true, attributeFilter: ['open'] });
        });
      });
    }

    namespaceSection(section) {
      const sectionId = section.getAttribute('data-section');
      if (!sectionId) return;
      const suffixed = sectionId + '-qv';
      const attributes = ['id', 'data-section', 'for', 'aria-controls', 'aria-describedby', 'data-modal'];
      [section, ...section.querySelectorAll('*')].forEach((el) => {
        attributes.forEach((name) => {
          const value = el.getAttribute && el.getAttribute(name);
          if (value && value.includes(sectionId)) {
            el.setAttribute(name, value.split(sectionId).join(suffixed));
          }
        });
      });
    }

    adoptStylesheets(doc) {
      const loads = [];
      doc.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
        const href = link.getAttribute('href');
        if (!href || !href.includes('/assets/')) return;
        const file = href.split('/').pop().split('?')[0];
        const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"][href]')).some((l) => {
          const lh = l.getAttribute('href') || '';
          return lh.split('/').pop().split('?')[0] === file;
        });
        if (existing) return;
        loads.push(
          new Promise((resolve) => {
            const clone = document.createElement('link');
            clone.rel = 'stylesheet';
            clone.href = href;
            clone.onload = resolve;
            clone.onerror = resolve;
            document.head.appendChild(clone);
            setTimeout(resolve, 3000);
          })
        );
      });
      return Promise.all(loads);
    }

  }

  customElements.define('cart-quick-view', CartQuickView);
}
